<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import { Link } from '@inertiajs/svelte';
    import { slide, fly } from 'svelte/transition';
    import ActivityFeed, { type ActivityItem } from '../../components/ActivityFeed.svelte';
    import StatCard from '../../components/StatCard.svelte';
    import echo from '../../echo';
    import { onDestroy } from 'svelte';
    import { show } from '@/routes/admin/cases';
    import { schedule } from '@/routes/admin';

    interface TodaysCase {
        id: number;
        history_excerpt: string;
        topic_name: string | null;
        image_url: string | null;
    }

    let { stats, todaysCases = [], tomorrowsCases = [], recentActivity: initialActivity }: {
        stats: any;
        todaysCases: TodaysCase[];
        tomorrowsCases: TodaysCase[];
        recentActivity: ActivityItem[];
    } = $props();

    const hasCases = $derived(todaysCases.length > 0 || tomorrowsCases.length > 0);
    let casesTab = $state<'today' | 'tomorrow'>(
        typeof window !== 'undefined' && sessionStorage.getItem('admin.casesTab') === 'tomorrow'
            ? 'tomorrow'
            : 'today'
    );

    $effect(() => {
        sessionStorage.setItem('admin.casesTab', casesTab);
    });
    let casesOpen = $state(typeof window !== 'undefined' && sessionStorage.getItem('admin.casesOpen') !== null
        ? sessionStorage.getItem('admin.casesOpen') === '1'
        : true
    );

    $effect(() => {
        sessionStorage.setItem('admin.casesOpen', casesOpen ? '1' : '0');
    });

    let activity = $state<ActivityItem[]>(initialActivity);

    const channel = echo.private('admin.activity');
    channel.listen('AnswerSubmitted', (e: { activity: ActivityItem }) => {
        if (e.activity) {
            activity = [e.activity, ...activity].slice(0, 20);
        }
    });

    onDestroy(() => {
        echo.leave('admin.activity');
    });
</script>

<svelte:head>
    <title>Dashboard — Admin — Case of the Day</title>
</svelte:head>

<h1 class="text-2xl font-bold text-gray-100 mb-6">Dashboard</h1>

    {#if hasCases}
        <div class="mb-6">
            <div class="flex items-center gap-3 mb-3">
                <button
                    onclick={() => casesOpen = !casesOpen}
                    class="text-gray-500 hover:text-gray-300 transition-colors"
                >
                    <svg class="size-3.5 transition-transform {casesOpen ? 'rotate-90' : ''}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>
                </button>
                <div class="flex items-center gap-1">
                    <button
                        onclick={() => { casesTab = 'today'; casesOpen = true; }}
                        class="text-sm font-medium uppercase tracking-wide px-2.5 py-1 rounded transition-colors {casesTab === 'today' ? 'text-cyan-400 bg-cyan-950' : 'text-gray-500 hover:text-gray-300'}"
                    >
                        Today ({todaysCases.length})
                    </button>
                    <button
                        onclick={() => { casesTab = 'tomorrow'; casesOpen = true; }}
                        class="text-sm font-medium uppercase tracking-wide px-2.5 py-1 rounded transition-colors {casesTab === 'tomorrow' ? 'text-cyan-400 bg-cyan-950' : 'text-gray-500 hover:text-gray-300'}"
                    >
                        Tomorrow ({tomorrowsCases.length})
                    </button>
                </div>
                <Link href={schedule()} class="text-xs text-gray-500 hover:text-gray-300 transition-colors">Full schedule &rarr;</Link>
            </div>
            {#if casesOpen}
                <div transition:slide={{ duration: 200 }} class="overflow-hidden relative">
                {#key casesTab}
                    {@const activeCases = casesTab === 'today' ? todaysCases : tomorrowsCases}
                    <div
                        in:fly={{ x: casesTab === 'tomorrow' ? 100 : -100, duration: 250, delay: 150 }}
                        out:fly|local={{ x: casesTab === 'tomorrow' ? -100 : 100, duration: 150 }}
                        class="out-abs"
                    >
                    {#if activeCases.length > 0}
                        <div class="flex gap-3 overflow-x-auto pb-2">
                            {#each activeCases as c (c.id)}
                                <Link
                                    href={show(c.id)}
                                    class="shrink-0 w-48 bg-gray-900 rounded-lg border border-gray-700 overflow-hidden hover:border-gray-600 transition-colors group"
                                >
                                    {#if c.image_url}
                                        <img src={c.image_url} alt="" class="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-200" />
                                    {:else}
                                        <div class="w-full aspect-[4/3] bg-gray-800 flex items-center justify-center text-gray-600">
                                            <svg class="size-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path opacity=".4" d="M0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32C28.7 32 0 60.7 0 96zM66.7 403.1c-4.1-7.9-3.5-17.5 1.6-24.8l56-80c4.5-6.4 11.8-10.2 19.7-10.2s15.2 3.8 19.7 10.2l26.4 37.8 61.4-100.5c4.4-7.1 12.1-11.5 20.5-11.5s16.1 4.4 20.5 11.5l88 144c4.5 7.4 4.7 16.7 .5 24.3S368.7 416 360 416L88 416c-8.9 0-17.2-5-21.3-12.9zM176 160a48 48 0 1 1 -96 0 48 48 0 1 1 96 0z"/><path fill="currentColor" d="M292.5 235.5c-4.4-7.1-12.1-11.5-20.5-11.5s-16.1 4.4-20.5 11.5L190.1 336 163.7 298.2c-4.5-6.4-11.8-10.2-19.7-10.2s-15.2 3.8-19.7 10.2l-56 80c-5.1 7.3-5.8 16.9-1.6 24.8S79.1 416 88 416l272 0c8.7 0 16.7-4.7 20.9-12.3s4.1-16.8-.5-24.3l-88-144z"/></svg>
                                        </div>
                                    {/if}
                                    <div class="p-2.5">
                                        {#if c.topic_name}
                                            <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-cyan-900 text-cyan-300 mb-1.5">
                                                <svg class="size-2.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path opacity=".4" d="M32.5 96l0 149.5c0 17 6.7 33.3 18.7 45.3l192 192c25 25 65.5 25 90.5 0L483.2 333.3c25-25 25-65.5 0-90.5l-192-192C279.2 38.7 263 32 246 32L96.5 32c-35.3 0-64 28.7-64 64zm112 16a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>
                                                {c.topic_name}
                                            </span>
                                        {/if}
                                        <p class="text-xs text-gray-400 line-clamp-2">{c.history_excerpt}</p>
                                    </div>
                                </Link>
                            {/each}
                        </div>
                    {:else}
                        <p class="text-sm text-gray-500">No cases scheduled for {casesTab === 'today' ? 'today' : 'tomorrow'}.</p>
                    {/if}
                    </div>
                {/key}
                </div>
            {/if}
        </div>
    {/if}

    <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard value={stats.today_users} color="amber" nablaClass="nabla-amber" subtitle="<strong>{stats.total_users}</strong> total">
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path opacity=".4" fill="currentColor" d="M0 416c0-70.7 57.3-128 128-128 12.8 0 25.2 1.9 36.9 5.4-32.9 36.8-52.9 85.4-52.9 138.6l0 16c0 11.4 2.4 22.2 6.7 32L32 480c-17.7 0-32-14.3-32-32l0-32zM168 160a72 72 0 1 1 -144 0 72 72 0 1 1 144 0zm448 0a72 72 0 1 1 -144 0 72 72 0 1 1 144 0zM475.1 293.4c11.7-3.5 24.1-5.4 36.9-5.4 70.7 0 128 57.3 128 128l0 32c0 17.7-14.3 32-32 32l-86.7 0c4.3-9.8 6.7-20.6 6.7-32l0-16c0-53.2-20-101.8-52.9-138.6z"/><path fill="currentColor" d="M160 432c0-88.4 71.6-160 160-160s160 71.6 160 160l0 16c0 17.7-14.3 32-32 32l-256 0c-17.7 0-32-14.3-32-32l0-16zm56-312a104 104 0 1 1 208 0 104 104 0 1 1 -208 0z"/></svg>{/snippet}
            {#snippet label()}Users Today{/snippet}
        </StatCard>
        <StatCard value={stats.today_cases} color="cyan" nablaClass="nabla-cyan" subtitle="<strong>{stats.total_cases}</strong> total">
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M96 64l0 288c0 35.3 28.7 64 64 64l181.5 0c17 0 33.3-6.7 45.3-18.7L493.3 290.7c12-12 18.7-28.3 18.7-45.3L512 64c0-35.3-28.7-64-64-64L160 0C124.7 0 96 28.7 96 64zM208 192c0-8.8 7.2-16 16-16l48 0 0-48c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 48 48 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-48 0 0 48c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-48-48 0c-8.8 0-16-7.2-16-16l0-32z"/><path fill="currentColor" d="M48 120c0-13.3-10.7-24-24-24S0 106.7 0 120L0 448c0 35.3 28.7 64 64 64l264 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L64 464c-8.8 0-16-7.2-16-16l0-328zm224 8l0 48-48 0c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l48 0 0 48c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-48 48 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-48 0 0-48c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z"/></svg>{/snippet}
            {#snippet label()}Cases Today{/snippet}
        </StatCard>
        <StatCard value={stats.today_answers} color="violet" nablaClass="nabla-violet" subtitle="<strong>{stats.total_answers}</strong> total">
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 160c0-53 43-96 96-96l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-96 0c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l256 0c17.7 0 32-14.3 32-32l0-96c0-17.7 14.3-32 32-32s32 14.3 32 32l0 96c0 53-43 96-96 96L96 512c-53 0-96-43-96-96L0 160z"/><path fill="currentColor" d="M392.4 21.7L368 46.1 465.9 144 490.3 119.6c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0zM334.1 80L172.4 241.7c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L432 177.9 334.1 80z"/></svg>{/snippet}
            {#snippet label()}Answers Today{/snippet}
        </StatCard>
        <StatCard value={stats.today_correct} color="emerald" nablaClass="nabla-emerald" subtitle="<strong>{stats.correct_answers}</strong> total">
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zm135.1 7.1c9.4-9.4 24.6-9.4 33.9 0L221.1 315.2 340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L221.1 315.2 340.5 151z"/></svg>{/snippet}
            {#snippet label()}Correct Today{/snippet}
        </StatCard>
        <StatCard value={stats.unscored_answers} color="gray" nablaClass="nabla-gray" subtitle="<strong>{stats.today_scored}</strong> scored today">
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path opacity=".4" fill="currentColor" d="M0 96L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-352c0-35.3-28.7-64-64-64l-8.6 0C300.4 12.9 279.7 0 256 0L128 0C104.3 0 83.6 12.9 72.6 32L64 32C28.7 32 0 60.7 0 96zM102.7 341.5c-9.2-9.5-9-24.7 .6-33.9s24.7-8.9 33.9 .6l26.5 27.4 85.6-117.7c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L185.8 386.9c-4.2 5.7-10.7 9.4-17.8 9.8s-14-2.2-18.9-7.3l-46.4-48zM112 88c0-13.3 10.7-24 24-24l112 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-112 0c-13.3 0-24-10.7-24-24z"/><path fill="currentColor" d="M249.4 217.9c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L185.8 386.9c-4.2 5.7-10.7 9.4-17.8 9.8s-14-2.2-18.9-7.3l-46.4-48c-9.2-9.5-9-24.7 .6-33.9s24.7-8.9 33.9 .6l26.5 27.4 85.6-117.7z"/></svg>{/snippet}
            {#snippet label()}To Score{/snippet}
        </StatCard>
        <StatCard value="" color="rose" nablaClass="nabla-rose nabla-dot-raised">
            {#snippet valueSnippet()}{#each stats.avg_answers_per_user.split('.') as part, i (i)}{#if i > 0}<span class="dot">.</span>{/if}{part}{/each}{/snippet}
            {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path opacity=".4" fill="currentColor" d="M128 96l0 49c5.2-.7 10.6-1 16-1 70.7 0 128 57.3 128 128 0 27.7-8.8 53.4-23.8 74.4 42.8 17.5 75.1 55.4 84.8 101.6l211 0c35.3 0 64-28.7 64-64l0-288c0-35.3-28.7-64-64-64L192 32c-35.3 0-64 28.7-64 64zM303 207c9.4-9.4 24.6-9.4 33.9 0l31 31 78.1-78.1-30.1 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l88 0c13.3 0 24 10.7 24 24l0 88c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-30.1-95 95c-4.5 4.5-10.6 7-17 7s-12.5-2.5-17-7l-48-48c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M144 352a80 80 0 1 0 0-160 80 80 0 1 0 0 160zM96 384c-53 0-96 43-96 96 0 17.7 14.3 32 32 32l224 0c17.7 0 32-14.3 32-32 0-53-43-96-96-96l-96 0z"/></svg>{/snippet}
            {#snippet label()}Answers <span class="text-xs">per</span> User{/snippet}
        </StatCard>
    </div>

    <div class="mt-10">
        <h2 class="text-lg font-semibold text-gray-100 mb-5">Recent Activity</h2>

        <ActivityFeed {activity} />
    </div>

<style>
    :global(.out-abs) {
        position: relative;
    }
    :global(.out-abs:not(:last-child)) {
        position: absolute;
        inset: 0;
        z-index: 0;
    }
</style>
